import {useCallback, useEffect, useState} from 'react';
import {UiBadge, UiButton, UiCard, UiNotice, UiStatus} from '@morefunos/ui';
import type {SmmActiveWorkPort, SmtP01ActiveWorkItemViewModel} from './smm-active-work-port.ts';

export interface ActiveWorkPageProps {
  readonly port: SmmActiveWorkPort;
}

const serviceModeLabel = (mode: string): string => mode === 'dine-in' ? '堂食' : mode === 'takeaway' ? '外帶' : mode;

const pricingLabel = (item: SmtP01ActiveWorkItemViewModel): string => {
  const pricing = item.pricing;
  if (pricing.status === 'quoted' && pricing.currency && pricing.totalMinor !== undefined) {
    return `${pricing.currency} ${(pricing.totalMinor / 100).toFixed(2)}`;
  }
  if (pricing.status === 'pending_required') return '尚有必選項目';
  if (pricing.status === 'blocked') return '價錢暫時受阻';
  if (pricing.status === 'rejected') return '報價被拒';
  if (pricing.status === 'unbound') return '價錢未綁定';
  return '未有報價';
};

const errorText = (error: unknown): string => error instanceof Error ? error.message : 'ACTIVE_WORK_UI_FAILED';

export function ActiveWorkPage({port}: ActiveWorkPageProps) {
  const [items, setItems] = useState<readonly SmtP01ActiveWorkItemViewModel[]>([]);
  const [feedback, setFeedback] = useState<{tone: 'positive' | 'critical' | 'info'; text: string} | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const current = port.currentWorkspace();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await port.readActiveWork();
      setItems(snapshot.items);
    } catch (error) {
      setFeedback({tone: 'critical', text: `讀取待處理失敗：${errorText(error)}`});
    } finally {
      setLoading(false);
    }
  }, [port]);

  useEffect(() => {
    void refresh();
  }, [refresh, refreshKey]);

  const refreshAfter = () => setRefreshKey((value) => value + 1);

  const openNew = () => {
    try {
      const opened = port.ensureWorkspace();
      setFeedback({tone: 'positive', text: `已開啟工作 ${opened.workspaceId}`});
      refreshAfter();
    } catch (error) {
      setFeedback({tone: 'critical', text: `開啟工作失敗：${errorText(error)}`});
    }
  };

  const holdCurrent = async () => {
    try {
      const result = await port.holdCurrent();
      setFeedback(result.kind === 'accepted'
        ? {tone: 'positive', text: '工作已釋放，可由其他裝置接手。'}
        : {tone: 'critical', text: `未能 Hold：${result.code}`});
    } catch (error) {
      setFeedback({tone: 'critical', text: `Hold 失敗：${errorText(error)}`});
    }
    refreshAfter();
  };

  const retrieve = async (item: SmtP01ActiveWorkItemViewModel) => {
    try {
      const result = await port.retrieve({
        workspaceId: item.workspaceId,
        workspaceRevision: item.workspaceRevision,
        custodyRevision: item.custodyRevision,
      });
      setFeedback(result.kind === 'accepted'
        ? {tone: 'positive', text: `已接手工作 ${item.workspaceId}`}
        : {tone: 'critical', text: `未能接手：${result.code}`});
    } catch (error) {
      setFeedback({tone: 'critical', text: `接手失敗：${errorText(error)}`});
    }
    refreshAfter();
  };

  return (
    <section className="smm-page" aria-labelledby="smm-active-work-title">
      <header className="smm-section-heading smm-section-heading--row">
        <div>
          <p className="smm-eyebrow">Active Work</p>
          <h1 id="smm-active-work-title">待處理</h1>
        </div>
        <UiButton variant="ghost" onPress={() => void refresh()} isDisabled={loading}>
          {loading ? '更新中…' : '更新'}
        </UiButton>
      </header>

      {feedback ? (
        <UiNotice tone={feedback.tone} role={feedback.tone === 'critical' ? 'alert' : 'status'} className="smm-feedback">
          {feedback.text}
        </UiNotice>
      ) : null}

      <UiCard className="smm-current-card">
        <div className="smm-card-title-row">
          <div>
            <p className="smm-card-kicker">本機工作</p>
            <h2>{current ? '進行中' : '未接手工作'}</h2>
          </div>
          <UiStatus tone={current ? 'positive' : 'neutral'}>{current ? 'SMM 已持有' : '可接新工作'}</UiStatus>
        </div>

        {current ? (
          <div className="smm-current-detail">
            <code>{current.workspaceId}</code>
            <div className="smm-chip-row">
              <UiBadge>{serviceModeLabel(current.serviceMode)}</UiBadge>
              <UiBadge>{current.lines.reduce((sum, line) => sum + line.quantity, 0)} 件</UiBadge>
              <UiBadge tone={current.pendingRequiredTaskCount > 0 ? 'warning' : 'positive'}>
                {current.pendingRequiredTaskCount > 0 ? `${current.pendingRequiredTaskCount} 個必選未完成` : '選項完整'}
              </UiBadge>
            </div>
            <UiButton variant="secondary" onPress={() => void holdCurrent()}>Hold／釋放</UiButton>
          </div>
        ) : (
          <div className="smm-current-detail">
            <p className="smm-muted">新點單會開一個由 C 線持有嘅 SMM workspace；B 唔保存第二份工作真相。</p>
            <UiButton variant="primary" onPress={openNew}>開始新工作</UiButton>
          </div>
        )}
      </UiCard>

      <div className="smm-list-heading">
        <h2>可接手／已被持有</h2>
        <span>{items.length} 項</span>
      </div>

      <div className="smm-work-list">
        {items.length === 0 && !loading ? (
          <UiCard className="smm-empty-card">
            <p>而家冇待處理工作。</p>
            <span className="smm-muted">有工作被 Hold 之後會喺呢度出現。</span>
          </UiCard>
        ) : null}

        {items.map((item) => {
          const available = item.status === 'available';
          const claimedBy = item.currentHandler?.actorId ?? item.lastHandler?.actorId ?? '—';
          return (
            <UiCard key={item.workspaceId} className="smm-work-card">
              <div className="smm-card-title-row">
                <div>
                  <p className="smm-card-kicker">{serviceModeLabel(item.serviceMode)} · {item.itemCount} 件</p>
                  <h3>{pricingLabel(item)}</h3>
                </div>
                <UiStatus tone={available ? 'info' : 'warning'}>{available ? '可接手' : '已被持有'}</UiStatus>
              </div>

              <code className="smm-work-id">{item.workspaceId}</code>
              <div className="smm-meta-grid">
                <span>Workspace rev. {item.workspaceRevision}</span>
                <span>Custody rev. {item.custodyRevision}</span>
                <span>Handler：{claimedBy}</span>
                <span>{item.pendingRequiredTaskCount > 0 ? `${item.pendingRequiredTaskCount} 個必選未完成` : '選項完整'}</span>
              </div>

              <UiButton
                variant={available ? 'primary' : 'secondary'}
                onPress={() => void retrieve(item)}
                isDisabled={!available || Boolean(port.currentWorkspaceId())}
              >
                {available ? (port.currentWorkspaceId() ? '先 Hold 本機工作' : '接手') : '不可搶單'}
              </UiButton>
            </UiCard>
          );
        })}
      </div>
    </section>
  );
}
